import guidesFr from "./guides/guides.fr.json";
import guidesEn from "./guides/guides.en.json";
import guidesEs from "./guides/guides.es.json";
import listFr from "./guides/guides-list.fr.json";
import listEn from "./guides/guides-list.en.json";
import listEs from "./guides/guides-list.es.json";

export interface Feature {
  title: string;
  desc: string;
}

export interface GuideData {
  /** Identifiant stable, identique sur les 3 langues (sert au mapping de slugs). */
  id: string;
  slug: string;
  title: string;
  metaDescription: string;
  intro: string;
  steps: { title: string; desc: string }[];
  features: Feature[];
  faq: { question: string; answer: string }[];
  ctaLabel: string;
  ctaHref: string;
}

export interface GuideListItem {
  id: string;
  slug: string;
  title: string;
  category: string;
}

type GuidesMap = Record<string, GuideData>;

const GUIDES_BY_LOCALE: Record<string, GuidesMap> = {
  fr: guidesFr as GuidesMap,
  en: guidesEn as GuidesMap,
  es: guidesEs as GuidesMap,
};

const LIST_BY_LOCALE: Record<string, GuideListItem[]> = {
  fr: listFr as GuideListItem[],
  en: listEn as GuideListItem[],
  es: listEs as GuideListItem[],
};

const SUPPORTED = ["fr", "en", "es"] as const;
const norm = (lang?: string) => {
  const l = (lang || "fr").slice(0, 2);
  return (SUPPORTED as readonly string[]).includes(l) ? l : "fr";
};

/** Guides map (slug → GuideData) for the given locale, fallback FR. */
export function getGuides(lang?: string): GuidesMap {
  return GUIDES_BY_LOCALE[norm(lang)] ?? GUIDES_BY_LOCALE.fr;
}

/** Guide catalog list for the given locale, fallback FR. */
export function getGuideList(lang?: string): GuideListItem[] {
  return LIST_BY_LOCALE[norm(lang)] ?? LIST_BY_LOCALE.fr;
}

/** A single guide by slug in the given locale (undefined if not found). */
export function getGuide(
  lang: string | undefined,
  slug: string,
): GuideData | undefined {
  return getGuides(lang)[slug];
}

/**
 * Trouve l'id stable d'un guide à partir d'un slug, quelle que soit la langue
 * dans laquelle ce slug existe.
 */
function findIdBySlug(slug: string): string | undefined {
  for (const loc of SUPPORTED) {
    const guide = GUIDES_BY_LOCALE[loc][slug];
    if (guide) return guide.id;
  }
  return undefined;
}

/**
 * Donne le slug équivalent d'un guide dans la langue cible.
 * Sert au changement de langue sur une page guide (les slugs sont localisés).
 * Renvoie undefined si le slug d'origine est inconnu.
 */
export function getEquivalentSlug(
  slug: string,
  targetLang: string,
): string | undefined {
  const id = findIdBySlug(slug);
  if (!id) return undefined;
  const guides = GUIDES_BY_LOCALE[norm(targetLang)];
  const match = Object.values(guides).find((g) => g.id === id);
  return match?.slug;
}
